import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { DatePicker } from '../components/ui/DatePicker';
import { InvoiceStatusBadge } from '../components/domain/StatusBadge';
import { QuoteApprovalModal } from '../components/domain/QuoteApprovalModal';
import { Modal } from '../components/ui/Modal';
import { Invoice } from '../types';
import { formatCurrency, formatDate } from '../utils/calculations';
import { PlusIcon, SearchIcon, EyeIcon, MailIcon, MessageSquareIcon, CheckCircleIcon, FilterIcon, Share2Icon } from 'lucide-react';
export const Invoices: React.FC = () => {
  const {
    filteredInvoices,
    getCustomerById,
    updateInvoice,
    getLineItemsByInvoiceId
  } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuoteApprovalModal, setShowQuoteApprovalModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareInvoiceId, setShareInvoiceId] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // Apply filter from navigation state (from Dashboard clicks)
  useEffect(() => {
    const state = location.state as {
      filterStatus?: string;
    } | null;
    if (state?.filterStatus) {
      setStatusFilter(state.filterStatus);
      // Clear the state so back button works normally
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Filter invoices
  const filteredData = useMemo(() => {
    return filteredInvoices.filter(invoice => {
      const customer = getCustomerById(invoice.customer_id);
      const searchMatch = !searchQuery || invoice.id.toLowerCase().includes(searchQuery.toLowerCase()) || customer?.name.toLowerCase().includes(searchQuery.toLowerCase());
      const statusMatch = statusFilter === 'all' || invoice.status === statusFilter;
      const invoiceDate = new Date(invoice.date);
      const startMatch = !startDate || invoiceDate >= new Date(startDate);
      const endMatch = !endDate || invoiceDate <= new Date(endDate);
      return searchMatch && statusMatch && startMatch && endMatch;
    });
  }, [filteredInvoices, searchQuery, statusFilter, startDate, endDate]);
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [filteredData]);
  const handleMarkPaid = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      await updateInvoice(invoice.id, {
        status: 'paid'
      });
    } catch (error) {
      console.error('Failed to update invoice:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendEmail = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      await updateInvoice(invoice.id, {
        sent_via_email: true,
        status: 'sent'
      });
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendSMS = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      await updateInvoice(invoice.id, {
        sent_via_sms: true,
        status: 'sent'
      });
    } catch (error) {
      console.error('Failed to send SMS:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleApproveQuote = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowQuoteApprovalModal(true);
  };
  const handleShareInvoice = (invoice: Invoice) => {
    setShareInvoiceId(invoice.id);
    setShareModalOpen(true);
    setCopySuccess(false);
  };
  const handleCopyLink = async () => {
    if (!shareInvoiceId) return;
    const shareUrl = `${window.location.origin}/public/invoice/${shareInvoiceId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => {
        setShareModalOpen(false);
        setCopySuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Please copy this link manually: ' + shareUrl);
    }
  };
  const handleViewLink = () => {
    if (!shareInvoiceId) return;
    window.open(`/public/invoice/${shareInvoiceId}`, '_blank');
    setShareModalOpen(false);
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'quote',
    label: 'Quote'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'approved',
    label: 'Approved'
  }, {
    value: 'refunded',
    label: 'Refunded'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  const columns = [{
    key: 'id',
    header: 'Invoice',
    render: (invoice: Invoice) => <Link to={`/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-411">
          {invoice.id.slice(0, 12)}...
        </Link>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: Invoice) => {
      const customer = getCustomerById(invoice.customer_id);
      return <span className="font-medium" data-id="element-412">{customer?.name || 'Unknown'}</span>;
    }
  }, {
    key: 'date',
    header: 'Date',
    render: (invoice: Invoice) => formatDate(invoice.date)
  }, {
    key: 'total',
    header: 'Total',
    render: (invoice: Invoice) => <span className="font-semibold" data-id="element-413">
          {formatCurrency(invoice.total_amount)}
        </span>
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: Invoice) => <InvoiceStatusBadge status={invoice.status} data-id="element-414" />
  }, {
    key: 'sent',
    header: 'Sent Via',
    render: (invoice: Invoice) => <div className="flex items-center gap-2" data-id="element-415">
          {invoice.sent_via_email && <span title="Email sent" className="text-green-600" data-id="element-416">
              <MailIcon className="w-4 h-4" data-id="element-417" />
            </span>}
          {invoice.sent_via_sms && <span title="SMS sent" className="text-green-600" data-id="element-418">
              <MessageSquareIcon className="w-4 h-4" data-id="element-419" />
            </span>}
          {!invoice.sent_via_email && !invoice.sent_via_sms && <span className="text-gray-400 text-sm" data-id="element-420">-</span>}
        </div>
  }, {
    key: 'actions',
    header: '',
    render: (invoice: Invoice) => <div className="flex items-center justify-end gap-1" data-id="element-421">
          <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleShareInvoice(invoice);
      }} className="text-gray-600 hover:text-blue-600 hover:bg-blue-50" aria-label="Share public link" title="Share public view" data-id="element-422">
            <Share2Icon className="w-4 h-4" data-id="element-423" />
          </Button>
          <Link to={`/invoices/${invoice.id}`} data-id="element-424">
            <Button variant="ghost" size="sm" aria-label="View invoice" data-id="element-425">
              <EyeIcon className="w-4 h-4" data-id="element-426" />
            </Button>
          </Link>
          {invoice.status === 'draft' && <>
              <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleSendEmail(invoice);
        }} disabled={isLoading} aria-label="Send via email" data-id="element-427">
                <MailIcon className="w-4 h-4" data-id="element-428" />
              </Button>
              <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleSendSMS(invoice);
        }} disabled={isLoading} aria-label="Send via SMS" data-id="element-429">
                <MessageSquareIcon className="w-4 h-4" data-id="element-430" />
              </Button>
            </>}
          {invoice.status === 'quote' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleApproveQuote(invoice);
      }} disabled={isLoading} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" aria-label="Approve quote" data-id="element-431">
              <CheckCircleIcon className="w-4 h-4" data-id="element-432" />
            </Button>}
          {invoice.status === 'sent' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleMarkPaid(invoice);
      }} disabled={isLoading} className="text-green-600 hover:text-green-700 hover:bg-green-50" aria-label="Mark as paid" data-id="element-433">
              <CheckCircleIcon className="w-4 h-4" data-id="element-434" />
            </Button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-435">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-436">
        <div data-id="element-437">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-438">Invoices</h1>
          <p className="text-gray-500 mt-1" data-id="element-439">
            {sortedData.length} invoice{sortedData.length !== 1 ? 's' : ''}{' '}
            found
          </p>
        </div>
        <Link to="/invoices/new" data-id="element-440">
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-442" />} data-id="element-441">
            New Invoice
          </Button>
        </Link>
      </div>

      <Card data-id="element-443">
        <div className="space-y-4" data-id="element-444">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-445">
            <div className="flex-1 relative" data-id="element-446">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-447" />
              <Input placeholder="Search by invoice ID or customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-448" />
            </div>
            <div className="w-full sm:w-48" data-id="element-449">
              <Select options={statusOptions} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-450" />
            </div>
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} leftIcon={<FilterIcon className="w-4 h-4" data-id="element-452" />} data-id="element-451">
              {showFilters ? 'Hide Filters' : 'More Filters'}
            </Button>
          </div>

          {showFilters && <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200" data-id="element-453">
              <div className="w-full sm:w-48" data-id="element-454">
                <DatePicker label="From Date" value={startDate} onChange={e => setStartDate(e.target.value)} data-id="element-455" />
              </div>
              <div className="w-full sm:w-48" data-id="element-456">
                <DatePicker label="To Date" value={endDate} onChange={e => setEndDate(e.target.value)} data-id="element-457" />
              </div>
              <div className="flex items-end" data-id="element-458">
                <Button variant="ghost" onClick={() => {
              setStartDate('');
              setEndDate('');
              setStatusFilter('all');
              setSearchQuery('');
            }} data-id="element-459">
                  Clear Filters
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Table columns={columns} data={sortedData} keyExtractor={inv => inv.id} emptyMessage="No invoices found" data-id="element-460" />

      <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Share Invoice" data-id="element-461">
        <div className="space-y-4" data-id="element-462">
          <p className="text-sm text-gray-600" data-id="element-463">
            Share this invoice with your customer using the link below:
          </p>

          {shareInvoiceId && <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-464">
              <code className="text-xs text-gray-700 break-all" data-id="element-465">
                {`${window.location.origin}/public/invoice/${shareInvoiceId}`}
              </code>
            </div>}

          <div className="flex gap-3 pt-2" data-id="element-466">
            <Button variant="primary" onClick={handleCopyLink} className="flex-1" data-id="element-467">
              {copySuccess ? <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-468">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-469" />
                  </svg>
                  Copied!
                </> : <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-470">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-471" />
                  </svg>
                  Copy Link
                </>}
            </Button>

            <Button variant="secondary" onClick={handleViewLink} className="flex-1" data-id="element-472">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-473">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-474" />
              </svg>
              View
            </Button>
          </div>
        </div>
      </Modal>

      {selectedInvoice && selectedInvoice.status === 'quote' && <QuoteApprovalModal invoice={selectedInvoice} lineItems={getLineItemsByInvoiceId(selectedInvoice.id)} isOpen={showQuoteApprovalModal} onClose={() => {
      setShowQuoteApprovalModal(false);
      setSelectedInvoice(null);
    }} onApproved={() => {
      setShowQuoteApprovalModal(false);
      setSelectedInvoice(null);
    }} data-id="element-475" />}
    </div>;
};